// Pub/sub for DDL invalidation events. Resolver services subscribe on
// creation; `ddlInvalidator.invalidateAfterDdl` (in
// `workspace/common/utils/ddlInvalidator.ts`) fires `notifyDdl(connectionId)`
// after a successful table/routine DDL query — the same helper that already
// calls `invalidateRoutines` + `invalidateColumns`. No parallel watcher; the
// Resolver re-pulls the SchemaSnapshot on the next read after a notify.

type Listener = () => void;

const listenersByConnection = new Map<string, Set<Listener>>();

export function subscribeToDdl(connectionId: string, listener: Listener): () => void {
  let set = listenersByConnection.get(connectionId);
  if (!set) {
    set = new Set();
    listenersByConnection.set(connectionId, set);
  }
  set.add(listener);
  return () => {
    const current = listenersByConnection.get(connectionId);
    if (!current) return;
    current.delete(listener);
    if (current.size === 0) listenersByConnection.delete(connectionId);
  };
}

export function notifyDdl(connectionId: string): void {
  const set = listenersByConnection.get(connectionId);
  if (!set) return;
  for (const listener of Array.from(set)) {
    try {
      listener();
    } catch {
      // One listener's error must not break the rest.
    }
  }
}

export function hasDdlListener(connectionId: string): boolean {
  return (listenersByConnection.get(connectionId)?.size ?? 0) > 0;
}

// Test-only: wipe module state between cases.
export function __resetDdlListenersForTest(): void {
  listenersByConnection.clear();
}
